import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import { Box, Typography, useTheme } from '@mui/material';

import { PKDFilledButton } from 'components/FilledButton';
import { PKDModal } from 'components/Modal';
import { PKDAccordion } from 'components/PKDAccordion';
import { TextInput } from 'components/PKDTextField';
import { Wrapper } from './styles';
import { updateSecondaryOverviewStep } from 'store/overview';
import { OutlineButton } from 'shared-components/Buttons/Outline';
import { ReactComponent as CheckCircleGreen } from 'images/shared/check-circle-green.svg';
import { useLogout } from 'hooks/useLogout';
import { useGetPersonAndAttributes } from 'hooks/useGetPersonAndAttributes';
import { openSnackbar } from 'store/application';
import { consentData } from 'pages/Consent/utils';
import { useGetEntityListQuery } from 'store/api/pkd/endpoints/entity';
import {
  useUpsertPersonAttributeMutation,
  useUpdateV3PersonMutation,
} from 'store/api/pkd/endpoints/person';
import {
  getAttributesPayload,
  getPersonAttribute,
  getPersonMedicalRecords,
} from 'pages/MedicalInformation/utils';
import { isBelowAge } from 'utils/date';
import { ErrorWrapper } from 'shared-components/ErrorWrapper';
import { PersonStatus } from 'utils/enum';

export const Modal = () => {
  const theme = useTheme();
  const { handleLogout } = useLogout();
  const dispatch = useDispatch();
  const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false);
  const [isWithdrawConfirmModalOpen, setIsWithdrawConfirmModalOpen] =
    useState(false);
  const [isWithdrawReasonModalOpen, setIsWithdrawReasonModalOpen] =
    useState(false);

  const { person, isPersonLoading, personId } = useGetPersonAndAttributes();
  const [updateV3Person, { isLoading: isPersonUpdating }] =
    useUpdateV3PersonMutation();
  const [upsertPersonAttribute, { isLoading: isPersonAttributeUpserting }] =
    useUpsertPersonAttributeMutation();

  const personInfo = getPersonMedicalRecords(person?.attributes, {
    date_of_birth : '',
    consentDate   : '',
  });
  let userIsBelow18 =
    personInfo?.date_of_birth && isBelowAge(personInfo?.date_of_birth, 18);
  const { isLoading: isEntityLoading } = useGetEntityListQuery(
    {
      entityName   : 'study_roles',
      entityParams : {
        personType : userIsBelow18 ? 'caregiver' : 'participant',
        studyId    : 1,
      },
    },
    { skip: isPersonLoading }
  );

  const {
    control,
    formState: { errors, isValid },
    handleSubmit,
    watch,
  } = useForm({
    mode          : 'onChange',
    defaultValues : {
      reason : '',
    },
  });

  const toggleWithdrawModal = () => setIsWithdrawModalOpen((prev) => !prev);
  const toggleWithdrawReasonModal = () => {
    setIsWithdrawModalOpen((prev) => !prev);
    setIsWithdrawReasonModalOpen((prev) => !prev);
  };

  const handleWithdrawConsent = handleSubmit(async (data) => {
    const attributes = getPersonAttribute(person?.attributes, {
      consentDate : '',
    });
    const body = getAttributesPayload({ consentDate: new Date() }, attributes);

    if (body.length > 0) {
      const { error: upsertPersonError } = await upsertPersonAttribute({
        body,
        personId,
      });
      let errorCount = upsertPersonError ? 1 : 0;
      // update v3 person
      const payload = {
        personId,
        attributes : {
          status       : PersonStatus.Withdrawn,
          statusReason : data?.reason !== '' ? data.reason : 'Participant declined consent',
        },
      };
      const { error: updatePersonError } = await updateV3Person(payload);
      errorCount = updatePersonError ? errorCount++ : errorCount;
      if (errorCount > 0) {
        return dispatch(
          openSnackbar({
            type : 'error',
            message :
              'Could not save your consent information. Please try again',
          })
        );
      }
    }

    dispatch(
      openSnackbar({
        type    : 'success',
        message : 'Consent information successfully saved',
      })
    );
    setIsWithdrawReasonModalOpen((prev) => !prev);
    setIsWithdrawConfirmModalOpen((prev) => !prev);
  });

  const handleLogoutUser = () => {
    setIsWithdrawConfirmModalOpen(false);
    handleLogout();
  };

  useEffect(() => {
    dispatch(updateSecondaryOverviewStep('consent'));
  });

  const hasWithdrawn = person.attributes.status === PersonStatus.Withdrawn;

  return (
    <Wrapper>
      <Box
        marginTop='40px'>
        <Box
          marginTop='34px'>
          <PKDAccordion
            data={consentData} hasDangerousElements={true}
          />
        </Box>
        <Box
          sx={{
            display        : 'flex',
            flexDirection  : 'column',
            alignItems     : 'center',
            justifyContent : 'center',
            paddingBottom  : '1.5rem',
          }}>
          <Typography
            fontSize='16px'
            lineHeight='24px'
            textAlign='center'
            mt={4}
            mb={4}>
            {hasWithdrawn ? 'Withdrawn' : 'Consent signed'} on:{' '}
            {personInfo?.consentDate}
          </Typography>
          <OutlineButton
            width='40%'
            fontFamily='Montserrat'
            color={theme.palette.primary.blue1}
            borderColor={theme.palette.primary.blue1}
            text={hasWithdrawn ? ' Withdrawn' : 'Withdraw from the study'}
            disabled={hasWithdrawn}
            onClickHandler={toggleWithdrawModal}
          />
        </Box>
      </Box>
      {/* withdraw modal */}
      <PKDModal
        isOpen={isWithdrawModalOpen}
        onClose={toggleWithdrawModal}
        title='Withdraw consent'>
        <Box
          padding='40px 16px'>
          <Typography
            fontSize='16px' lineHeight='24px' textAlign='center'>
            Are you sure you want to withdraw your consent?
          </Typography>
        </Box>
        <Box
          margin='24px auto' display='flex' justifyContent='center'>
          <PKDFilledButton
            sx={{
              backgroundColor : theme.palette.primary.blue1,
              color           : theme.palette.primary.white1,
              marginRight     : '8px',
              width           : '180px',
            }}
            onClick={toggleWithdrawReasonModal}>
            Confirm
          </PKDFilledButton>
          <PKDFilledButton
            sx={{
              backgroundColor : theme.palette.primary.grey3,
              color           : theme.palette.primary.gray7,
              marginLeft      : '8px',
              width           : '180px',
            }}
            onClick={() => setIsWithdrawModalOpen(false)}>
            Cancel
          </PKDFilledButton>
        </Box>
      </PKDModal>
      {/* withdraw reason modal */}
      <PKDModal
        isOpen={isWithdrawReasonModalOpen}
        onClose={() => setIsWithdrawReasonModalOpen(false)}
        title='Withdraw consent'>
        <Box
          sx={{
            display        : 'flex',
            flexDirection  : 'column',
            justifyContent : 'center',
            alignItems     : 'center',
            padding        : '40px 16px',
          }}>
          <Typography
            fontSize='16px'
            lineHeight='24px'
            textAlign='center'
            mb={4}>
            Please let us know why you withdrew from this study(optional)
          </Typography>
          <form
            style={{ width: '100%' }}
            onSubmit={handleSubmit(handleWithdrawConsent)}>
            <Controller
              control={control}
              name='reason'
              render={({ field: { onChange } }) => (
                <ErrorWrapper
                  message={errors.reason}>
                  <TextInput
                    value={watch('reason')}
                    multiline
                    rows={5}
                    onChange={onChange}
                    label='Type Answer Here'
                    placeholder='Name of Parent/Legal Guardian'
                    fullWidth
                    error={!!errors?.reason?.message}
                    hasErrors={
                      errors.reason && {
                        type    : 'custom',
                        message : errors.reason.message,
                      }
                    }
                  />
                </ErrorWrapper>
              )}
            />
          </form>
        </Box>
        <Box
          margin='24px auto' display='flex' justifyContent='center'>
          <PKDFilledButton
            sx={{
              backgroundColor : theme.palette.primary.blue1,
              color           : theme.palette.primary.white1,
              marginRight     : '8px',
              width           : '180px',
            }}
            isLoading={isPersonUpdating || isPersonAttributeUpserting}
            disabled={
              isPersonUpdating ||
              isEntityLoading ||
              !isValid ||
              isPersonAttributeUpserting
            }
            onClick={handleWithdrawConsent}>
            Confirm
          </PKDFilledButton>
          <PKDFilledButton
            sx={{
              backgroundColor : theme.palette.primary.grey3,
              color           : theme.palette.primary.gray7,
              marginLeft      : '8px',
              width           : '180px',
            }}
            onClick={() => setIsWithdrawReasonModalOpen(false)}>
            Cancel
          </PKDFilledButton>
        </Box>
      </PKDModal>
      {/* Withdrawal confirmation */}
      <PKDModal
        isOpen={isWithdrawConfirmModalOpen}
        onClose={handleLogoutUser}
        title='Withdraw consent'>
        <Box
          sx={{
            display        : 'flex',
            flexDirection  : 'column',
            justifyContent : 'center',
            alignItems     : 'center',
            padding        : '40px 16px',
          }}>
          <CheckCircleGreen
            width='100px' height='100px'
          />
          <Typography
            fontSize='20px'
            lineHeight='24px'
            fontWeight='800'
            textAlign='center'
            mt={2}
            mb={2}>
            You have been withdrawn
          </Typography>
        </Box>
        <Box
          margin='24px auto' display='flex' justifyContent='center'>
          <PKDFilledButton
            sx={{
              backgroundColor : theme.palette.primary.blue1,
              color           : theme.palette.primary.white1,
              marginRight     : '8px',
              width           : '180px',
            }}
            onClick={handleLogoutUser}>
            OK
          </PKDFilledButton>
        </Box>
      </PKDModal>
    </Wrapper>
  );
};
